import { createLogger } from '@/lib/logger';
import type { User } from '@scrambl/shared';

const logger = createLogger('lib/utils/tauri');

/**
 * Tauri detection and utility functions
 * This file provides helpers to detect when the app is running in Tauri desktop environment
 * and conditionally use native features
 */

/**
 * Check if the app is running inside Tauri
 */
export function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

/**
 * Check if the app is running in a browser
 */
export function isBrowser(): boolean {
  return !isTauri();
}

/**
 * Get the platform the app is running on
 */
export function getPlatform(): 'desktop' | 'web' {
  return isTauri() ? 'desktop' : 'web';
}

/**
 * Auth tokens interface for desktop storage
 */
export interface DesktopAuthTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt?: number; // Unix timestamp when access token expires
}

export interface DesktopAccountSummary {
  id: string;
  userId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  image?: string | null;
  lastUsedAt?: number;
}

interface DesktopAccountRecord extends DesktopAccountSummary {
  tokens: DesktopAuthTokens;
}

interface DesktopAccountInput {
  id?: string;
  userId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  image?: string | null;
  user?: User | null;
}

interface JwtProfilePayload {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
}

const EMAIL_ACCOUNT_PREFIX = 'email:';
const UNKNOWN_ACCOUNT_PREFIX = 'unknown:';

function normalizeEmail(email?: string | null): string | undefined {
  if (!email) return undefined;
  return email.trim().toLowerCase();
}

function decodeJwtPayload<T>(token?: string): T | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    const padded = padding ? `${base64}${'='.repeat(4 - padding)}` : base64;
    return JSON.parse(atob(padded)) as T;
  } catch {
    return null;
  }
}

function mergeDefined<T extends object>(base: T, overrides: Partial<T>): T {
  const result = { ...base };
  Object.entries(overrides).forEach(([key, value]) => {
    if (value !== undefined) {
      (result as Record<string, unknown>)[key] = value;
    }
  });
  return result;
}

/**
 * Desktop secure storage for auth tokens using Tauri's store plugin
 * Similar to mobile's secure storage but uses Tauri's persistent store
 */
class DesktopSecureStorage {
  private static instance: DesktopSecureStorage | null = null;
  private store: Awaited<ReturnType<typeof import('@tauri-apps/plugin-store').load>> | null = null;
  private initPromise: Promise<void> | null = null;
  private readonly STORE_NAME = 'auth.json';
  private readonly TOKENS_KEY = 'auth_tokens';
  private readonly ACCOUNTS_KEY = 'auth_accounts';
  private readonly ACTIVE_ACCOUNT_KEY = 'auth_active_account_id';

  public static getInstance(): DesktopSecureStorage {
    if (!DesktopSecureStorage.instance) {
      DesktopSecureStorage.instance = new DesktopSecureStorage();
    }
    return DesktopSecureStorage.instance;
  }

  /**
   * Initialize the store lazily
   */
  private async init(): Promise<void> {
    if (this.store) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      if (!isTauri()) {
        logger.warn('[DesktopSecureStorage] Not running in Tauri, storage will not work');
        return;
      }

      try {
        const { load } = await import('@tauri-apps/plugin-store');
        this.store = await load(this.STORE_NAME, { defaults: {}, autoSave: true });
        await this.migrateLegacyTokensIfNeeded();
      } catch (error) {
        logger.error('[DesktopSecureStorage] Failed to initialize store:', error);
        throw error;
      }
    })();

    return this.initPromise;
  }

  private buildProfile(input?: DesktopAccountInput, tokens?: DesktopAuthTokens): DesktopAccountInput {
    const fromTokens = this.profileFromTokens(tokens);
    const fromUser = input?.user
      ? {
          userId: input.user.id,
          email: input.user.email,
          displayName: input.user.displayName ?? undefined,
          firstName: input.user.firstName ?? undefined,
          lastName: input.user.lastName ?? undefined,
          image: input.user.image ?? undefined,
        }
      : {};
    const fromInput = { ...input, user: undefined };
    const merged = mergeDefined(mergeDefined(fromTokens, fromUser), fromInput);
    if (merged.email) {
      merged.email = normalizeEmail(merged.email);
    }
    return merged;
  }

  private profileFromTokens(tokens?: DesktopAuthTokens): DesktopAccountInput {
    if (!tokens) return {};
    const payload = decodeJwtPayload<JwtProfilePayload>(tokens.idToken || tokens.accessToken);
    if (!payload) return {};
    return {
      userId: payload.sub,
      email: payload.email ? normalizeEmail(payload.email) : undefined,
      displayName: payload.name,
      firstName: payload.given_name,
      lastName: payload.family_name,
      image: payload.picture,
    };
  }

  private resolveAccountId(profile: DesktopAccountInput, fallbackId?: string | null): string {
    const normalizedEmail = normalizeEmail(profile.email);
    return (
      profile.userId ||
      profile.id ||
      (normalizedEmail ? `${EMAIL_ACCOUNT_PREFIX}${normalizedEmail}` : '') ||
      fallbackId ||
      `${UNKNOWN_ACCOUNT_PREFIX}${Date.now()}`
    );
  }

  private async migrateLegacyTokensIfNeeded(): Promise<void> {
    if (!this.store) return;
    try {
      const legacyTokens = await this.store.get<DesktopAuthTokens>(this.TOKENS_KEY);
      const existingAccounts = await this.store.get<Record<string, DesktopAccountRecord>>(this.ACCOUNTS_KEY);
      if (!legacyTokens) return;

      const accounts = existingAccounts ?? {};
      if (Object.keys(accounts).length > 0) {
        await this.store.delete(this.TOKENS_KEY);
        await this.store.save();
        return;
      }

      const profile = this.buildProfile({}, legacyTokens);
      const accountId = this.resolveAccountId(profile, null);
      accounts[accountId] = {
        id: accountId,
        userId: profile.userId,
        email: profile.email,
        displayName: profile.displayName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        image: profile.image,
        tokens: legacyTokens,
        lastUsedAt: Date.now(),
      };
      await this.store.set(this.ACCOUNTS_KEY, accounts);
      await this.store.set(this.ACTIVE_ACCOUNT_KEY, accountId);
      await this.store.delete(this.TOKENS_KEY);
      await this.store.save();
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to migrate legacy tokens:', error);
    }
  }

  private normalizeAccounts(
    accounts: Record<string, DesktopAccountRecord>,
    activeId: string | null
  ): { accounts: Record<string, DesktopAccountRecord>; activeId: string | null; changed: boolean } {
    let changed = false;
    const deduped = new Map<string, DesktopAccountRecord>();
    const aliases = new Map<string, string>();

    const hasValue = (value: string | null | undefined) => Boolean(value && value.trim().length > 0);
    const profileScore = (account: DesktopAccountRecord) => {
      let score = 0;
      if (hasValue(account.email)) score += 3;
      if (hasValue(account.userId)) score += 2;
      if (hasValue(account.displayName)) score += 1;
      if (hasValue(account.firstName)) score += 1;
      if (hasValue(account.lastName)) score += 1;
      if (hasValue(account.image)) score += 1;
      if (account.id.startsWith(EMAIL_ACCOUNT_PREFIX)) score += 1;
      if (account.id.startsWith(UNKNOWN_ACCOUNT_PREFIX)) score -= 1;
      return score;
    };

    const mergeAccounts = (primary: DesktopAccountRecord, secondary: DesktopAccountRecord): DesktopAccountRecord => ({
      ...primary,
      userId: primary.userId ?? secondary.userId,
      email: primary.email ?? secondary.email,
      displayName: primary.displayName ?? secondary.displayName,
      firstName: primary.firstName ?? secondary.firstName,
      lastName: primary.lastName ?? secondary.lastName,
      image: primary.image ?? secondary.image,
      lastUsedAt: Math.max(primary.lastUsedAt ?? 0, secondary.lastUsedAt ?? 0),
    });

    const pickPrimary = (a: DesktopAccountRecord, b: DesktopAccountRecord) => {
      if (a.id === activeId) return a;
      if (b.id === activeId) return b;
      const aUsed = a.lastUsedAt ?? 0;
      const bUsed = b.lastUsedAt ?? 0;
      if (aUsed !== bUsed) return aUsed > bUsed ? a : b;
      return profileScore(a) >= profileScore(b) ? a : b;
    };

    const buildKeys = (account: DesktopAccountRecord) => {
      const normalizedEmail = normalizeEmail(account.email);
      return {
        emailKey: normalizedEmail ? `${EMAIL_ACCOUNT_PREFIX}${normalizedEmail}` : null,
        userKey: account.userId ? `user:${account.userId}` : null,
        idKey: `id:${account.id}`,
      };
    };

    const resolveCanonicalKey = (keys: Array<string | null>) => {
      for (const key of keys) {
        if (!key) continue;
        if (deduped.has(key)) return key;
        const alias = aliases.get(key);
        if (alias && deduped.has(alias)) return alias;
      }
      return null;
    };

    const registerAliases = (
      canonicalKey: string,
      keys: { emailKey: string | null; userKey: string | null; idKey: string }
    ) => {
      if (keys.emailKey) aliases.set(keys.emailKey, canonicalKey);
      if (keys.userKey) aliases.set(keys.userKey, canonicalKey);
      aliases.set(keys.idKey, canonicalKey);
    };

    Object.values(accounts).forEach((account) => {
      const isEmptyProfile =
        !hasValue(account.email) &&
        !hasValue(account.userId) &&
        !hasValue(account.displayName) &&
        !hasValue(account.firstName) &&
        !hasValue(account.lastName) &&
        !hasValue(account.image);

      if (isEmptyProfile && account.id !== activeId) {
        changed = true;
        return;
      }

      const keys = buildKeys(account);
      const canonicalKey =
        resolveCanonicalKey([keys.emailKey, keys.userKey, keys.idKey]) ??
        (keys.emailKey ?? keys.userKey ?? keys.idKey);
      const existing = deduped.get(canonicalKey);
      if (!existing) {
        deduped.set(canonicalKey, account);
        registerAliases(canonicalKey, keys);
        return;
      }

      const primary = pickPrimary(existing, account);
      const secondary = primary.id === existing.id ? account : existing;
      const merged = mergeAccounts(primary, secondary);
      deduped.set(canonicalKey, merged);
      registerAliases(canonicalKey, keys);
      registerAliases(canonicalKey, buildKeys(merged));
      changed = true;
    });

    const normalizedAccounts: Record<string, DesktopAccountRecord> = {};
    deduped.forEach((account) => {
      normalizedAccounts[account.id] = account;
    });

    let normalizedActiveId = activeId;
    if (normalizedActiveId && !normalizedAccounts[normalizedActiveId]) {
      const ids = Object.keys(normalizedAccounts);
      normalizedActiveId = ids.length === 1 ? ids[0] : null;
      changed = true;
    }

    return { accounts: normalizedAccounts, activeId: normalizedActiveId, changed };
  }

  private async readAccounts(): Promise<Record<string, DesktopAccountRecord>> {
    await this.init();
    if (!this.store) return {};
    await this.store.reload();
    await this.migrateLegacyTokensIfNeeded();
    const accounts = await this.store.get<Record<string, DesktopAccountRecord>>(this.ACCOUNTS_KEY);
    const activeId = await this.readActiveAccountId();
    const normalized = this.normalizeAccounts(accounts ?? {}, activeId);
    if (normalized.changed) {
      await this.store.set(this.ACCOUNTS_KEY, normalized.accounts);
      await this.store.set(this.ACTIVE_ACCOUNT_KEY, normalized.activeId);
      await this.store.save();
    }
    return normalized.accounts;
  }

  private async writeAccounts(accounts: Record<string, DesktopAccountRecord>): Promise<void> {
    if (!this.store) return;
    await this.store.set(this.ACCOUNTS_KEY, accounts);
    await this.store.save();
  }

  private async readActiveAccountId(): Promise<string | null> {
    if (!this.store) return null;
    const activeId = await this.store.get<string>(this.ACTIVE_ACCOUNT_KEY);
    return activeId ?? null;
  }

  /**
   * Save auth tokens to secure storage
   */
  async saveTokens(tokens: DesktopAuthTokens, input?: DesktopAccountInput): Promise<void> {
    if (!isTauri()) return;

    try {
      await this.init();
      if (!this.store) return;

      await this.store.reload();
      await this.migrateLegacyTokensIfNeeded();
      const accounts = await this.readAccounts();
      let activeId = await this.readActiveAccountId();
      if (!activeId && Object.keys(accounts).length === 1) {
        activeId = Object.keys(accounts)[0];
      }
      const profile = this.buildProfile(input, tokens);
      const accountId = this.resolveAccountId(profile, activeId);
      const existing = accounts[accountId];
      const lastUsedAt = Date.now();
      const derivedUserId =
        profile.userId ??
        existing?.userId ??
        (accountId.startsWith(EMAIL_ACCOUNT_PREFIX) || accountId.startsWith(UNKNOWN_ACCOUNT_PREFIX)
          ? undefined
          : accountId);

      // Preserve existing token fields when partial updates are provided.
      // This prevents losing a valid refresh token if an update only includes an access token.
      const incomingRefreshToken =
        typeof tokens.refreshToken === 'string' && tokens.refreshToken.trim().length > 0
          ? tokens.refreshToken
          : undefined;
      const incomingIdToken =
        typeof tokens.idToken === 'string' && tokens.idToken.trim().length > 0
          ? tokens.idToken
          : undefined;

      const mergedTokens: DesktopAuthTokens = {
        accessToken: tokens.accessToken,
        refreshToken: incomingRefreshToken ?? existing?.tokens?.refreshToken,
        idToken: incomingIdToken ?? existing?.tokens?.idToken,
        expiresAt: tokens.expiresAt ?? existing?.tokens?.expiresAt,
      };

      accounts[accountId] = {
        id: accountId,
        userId: derivedUserId,
        email: profile.email ?? existing?.email,
        displayName: profile.displayName ?? existing?.displayName,
        firstName: profile.firstName ?? existing?.firstName,
        lastName: profile.lastName ?? existing?.lastName,
        image: profile.image ?? existing?.image,
        tokens: mergedTokens,
        lastUsedAt,
      };

      await this.store.set(this.ACTIVE_ACCOUNT_KEY, accountId);
      await this.writeAccounts(accounts);
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to save tokens:', error);
      throw error;
    }
  }

  /**
   * Get auth tokens from secure storage
   * Note: Each window has its own JS context, so we reload from disk to ensure fresh data
   */
  async getTokens(): Promise<DesktopAuthTokens | null> {
    if (!isTauri()) return null;

    try {
      const accounts = await this.readAccounts();
      const activeId = await this.readActiveAccountId();
      if (!activeId || !accounts[activeId]) {
        if (Object.keys(accounts).length === 1) {
          const onlyId = Object.keys(accounts)[0];
          if (this.store) {
            await this.store.set(this.ACTIVE_ACCOUNT_KEY, onlyId);
            await this.store.save();
          }
          return accounts[onlyId]?.tokens ?? null;
        }
        return null;
      }
      return accounts[activeId]?.tokens ?? null;
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to get tokens:', error);
      return null;
    }
  }

  /**
   * Delete auth tokens from secure storage
   */
  async deleteTokens(): Promise<void> {
    if (!isTauri()) return;

    try {
      await this.init();
      if (!this.store) return;
      await this.store.reload();
      await this.migrateLegacyTokensIfNeeded();
      const accounts = await this.readAccounts();
      let activeId = await this.readActiveAccountId();
      if (!activeId && Object.keys(accounts).length === 1) {
        activeId = Object.keys(accounts)[0];
      }

      if (activeId && accounts[activeId]) {
        delete accounts[activeId];
      }
      await this.store.set(this.ACTIVE_ACCOUNT_KEY, null);
      await this.writeAccounts(accounts);
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to delete tokens:', error);
      throw error;
    }
  }

  async clearAllAccounts(): Promise<void> {
    if (!isTauri()) return;

    try {
      await this.init();
      if (!this.store) return;
      await this.store.delete(this.ACTIVE_ACCOUNT_KEY);
      await this.store.delete(this.ACCOUNTS_KEY);
      await this.store.delete(this.TOKENS_KEY);
      await this.store.save();
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to clear accounts:', error);
      throw error;
    }
  }

  async getAccounts(): Promise<DesktopAccountSummary[]> {
    if (!isTauri()) return [];
    try {
      const accounts = await this.readAccounts();
      // Sort alphabetically by email for a stable, predictable order
      return Object.values(accounts)
        .map(({ tokens: _tokens, ...summary }) => summary)
        .sort((a, b) => {
          const emailA = (a.email ?? '').toLowerCase();
          const emailB = (b.email ?? '').toLowerCase();
          return emailA.localeCompare(emailB);
        });
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to load accounts:', error);
      return [];
    }
  }

  async getActiveAccountId(): Promise<string | null> {
    if (!isTauri()) return null;
    try {
      const accounts = await this.readAccounts();
      const activeId = await this.readActiveAccountId();
      if (!activeId || !accounts[activeId]) {
        if (Object.keys(accounts).length === 1) {
          const onlyId = Object.keys(accounts)[0];
          if (this.store) {
            await this.store.set(this.ACTIVE_ACCOUNT_KEY, onlyId);
            await this.store.save();
          }
          return onlyId;
        }
        return null;
      }
      return activeId;
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to get active account:', error);
      return null;
    }
  }

  async setActiveAccount(accountId: string): Promise<boolean> {
    if (!isTauri()) return false;
    try {
      await this.init();
      if (!this.store) return false;
      await this.store.reload();
      await this.migrateLegacyTokensIfNeeded();
      const accounts = await this.readAccounts();

      if (!accounts[accountId]) {
        return false;
      }

      accounts[accountId] = {
        ...accounts[accountId],
        lastUsedAt: Date.now(),
      };
      await this.store.set(this.ACTIVE_ACCOUNT_KEY, accountId);
      await this.writeAccounts(accounts);
      return true;
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to set active account:', error);
      return false;
    }
  }

  async removeAccount(accountId: string): Promise<void> {
    if (!isTauri()) return;
    try {
      await this.init();
      if (!this.store) return;
      await this.store.reload();
      await this.migrateLegacyTokensIfNeeded();
      const accounts = await this.readAccounts();
      if (!accounts[accountId]) return;
      delete accounts[accountId];
      const activeId = await this.readActiveAccountId();
      if (activeId === accountId) {
        await this.store.set(this.ACTIVE_ACCOUNT_KEY, null);
      }
      await this.writeAccounts(accounts);
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to remove account:', error);
      throw error;
    }
  }

  async upsertAccountProfile(user: User): Promise<void> {
    if (!isTauri()) return;
    try {
      await this.init();
      if (!this.store) return;
      await this.store.reload();
      await this.migrateLegacyTokensIfNeeded();

      const accounts = await this.readAccounts();
      const activeId = await this.readActiveAccountId();
      const profile = this.buildProfile({ user }, undefined);
      const targetId = this.resolveAccountId(profile, activeId);

      const normalizedEmail = normalizeEmail(profile.email);
      const emailId = normalizedEmail ? `${EMAIL_ACCOUNT_PREFIX}${normalizedEmail}` : null;

      // The active record is the token source for the user who just completed
      // authentication. Prefer it over an older canonical profile record so a
      // successful OAuth reauthentication cannot be overwritten by stale tokens.
      const sourceId =
        (activeId && accounts[activeId] ? activeId : null) ||
        (targetId && accounts[targetId] ? targetId : null) ||
        (emailId && accounts[emailId] ? emailId : null);

      if (!sourceId) return;

      const source = accounts[sourceId];
      const existingTarget = accounts[targetId];
      const merged: DesktopAccountRecord = {
        ...existingTarget,
        ...source,
        id: targetId,
        userId: profile.userId ?? source.userId,
        email: profile.email ?? source.email,
        displayName: profile.displayName ?? source.displayName,
        firstName: profile.firstName ?? source.firstName,
        lastName: profile.lastName ?? source.lastName,
        image: profile.image ?? source.image,
        lastUsedAt: Math.max(source.lastUsedAt ?? 0, Date.now()),
      };

      // Remove records that identify the same authenticated account. This
      // covers the common OAuth transition from a Cognito-sub keyed record to
      // the canonical database user id without leaving stale-token aliases.
      Object.entries(accounts).forEach(([accountId, account]) => {
        if (accountId === targetId) return;
        const sameSource = accountId === sourceId;
        const sameUser = Boolean(source.userId && account.userId === source.userId);
        const sameEmail = Boolean(
          normalizedEmail && normalizeEmail(account.email) === normalizedEmail
        );
        if (sameSource || sameUser || sameEmail) {
          delete accounts[accountId];
        }
      });

      accounts[targetId] = merged;
      await this.store.set(this.ACTIVE_ACCOUNT_KEY, targetId);
      await this.writeAccounts(accounts);
    } catch (error) {
      logger.error('[DesktopSecureStorage] Failed to update account profile:', error);
    }
  }

  /**
   * Check if tokens exist in storage
   */
  async hasTokens(): Promise<boolean> {
    const tokens = await this.getTokens();
    return tokens !== null && !!tokens.accessToken && !!tokens.refreshToken;
  }
}

// Export singleton instance
export const desktopSecureStorage = DesktopSecureStorage.getInstance();

/**
 * Desktop auth callback listener
 * Listens for deep link auth callbacks (scrambl://auth/callback?...)
 */
export type DeepLinkAuthCallback = (url: string) => void;

let deepLinkAuthListeners: DeepLinkAuthCallback[] = [];
let deepLinkListenerSetup = false;

/**
 * Set up the deep link auth listener (call once on app startup)
 */
export async function setupDeepLinkAuthListener(): Promise<void> {
  if (!isTauri() || deepLinkListenerSetup) return;

  // Only listen for deep links in the main window — popouts and focus
  // windows should not navigate when a deep link is received.
  const isMain = await isMainWindow();
  if (!isMain) return;

  try {
    const { listen } = await import('@tauri-apps/api/event');
    
    await listen<string>('deep-link-auth', async (event) => {
      
      // If there are registered listeners (active auth flow), notify them
      if (deepLinkAuthListeners.length > 0) {
        deepLinkAuthListeners.forEach(callback => {
          try {
            callback(event.payload);
          } catch (error) {
            logger.error('[Desktop Auth] Callback error:', error);
          }
        });
      } else {

        const tokens = parseDeepLinkAuthTokens(event.payload);
        if (tokens) {
          try {
            await desktopSecureStorage.saveTokens(tokens);

            // Navigate to trigger auth check - use window.location for a full reload
            // This ensures AuthProvider picks up the new tokens
            const currentPath = window.location.pathname;
            if (currentPath.includes('/login') || currentPath.includes('/register') || currentPath === '/') {
              window.location.href = '/today';
            } else {
              window.location.reload();
            }
          } catch (error) {
            logger.error('[Desktop Auth] Failed to save tokens from direct deep link:', error);
          }
        } else {
          // Not an auth callback — try to navigate to the requested path
          const navPath = parseDeepLinkNavigationPath(event.payload);
          if (navPath) {
            logger.info('[Desktop] Navigating to deep link path:', navPath);
            window.location.href = navPath;
          }
        }
      }
    });
    
    deepLinkListenerSetup = true;
  } catch (error) {
    logger.error('[Desktop Auth] Failed to set up deep link listener:', error);
  }
}

/**
 * Register a callback for deep link auth events
 * @returns Unsubscribe function
 */
export function onDeepLinkAuth(callback: DeepLinkAuthCallback): () => void {
  deepLinkAuthListeners.push(callback);
  
  return () => {
    deepLinkAuthListeners = deepLinkAuthListeners.filter(cb => cb !== callback);
  };
}

/**
 * Extract a navigation path from a deep link URL.
 * For URLs like scrambl:///acme/tasks?task=123, returns /acme/tasks?task=123
 * Returns null if the URL is an auth callback or cannot be parsed.
 */
export function parseDeepLinkNavigationPath(url: string): string | null {
  try {
    const parsedUrl = new URL(url);

    // Auth callbacks are handled separately
    if (parsedUrl.host === 'auth' || parsedUrl.pathname.startsWith('/auth/callback')) {
      return null;
    }

    // Build the path: host acts as the first path segment for scrambl://org-slug/...
    // For scrambl:///path (triple slash), host is empty and pathname has the full path
    let path = '';
    if (parsedUrl.host && parsedUrl.host !== 'localhost') {
      path = '/' + parsedUrl.host;
    }
    path += parsedUrl.pathname;

    // Append query string if present
    if (parsedUrl.search) {
      path += parsedUrl.search;
    }

    // Don't navigate to empty or root-only paths
    if (!path || path === '/') {
      return null;
    }

    return path;
  } catch (error) {
    logger.error('[Desktop] Failed to parse deep link navigation URL:', error);
    return null;
  }
}

/**
 * Parse auth tokens from a deep link URL
 * Expected format: scrambl://auth/callback?accessToken=...&refreshToken=...&expiresIn=...
 */
export function parseDeepLinkAuthTokens(url: string): DesktopAuthTokens | null {
  try {
    const parsedUrl = new URL(url);
    
    // Check if this is an auth callback
    if (parsedUrl.host !== 'auth' && !parsedUrl.pathname.startsWith('/auth/callback')) {
      return null;
    }
    
    const params = parsedUrl.searchParams;
    const accessToken = params.get('accessToken');
    const refreshToken = params.get('refreshToken');
    const expiresIn = params.get('expiresIn');
    
    if (!accessToken) {
      logger.error('[Desktop Auth] No access token in deep link');
      return null;
    }
    
    const tokens: DesktopAuthTokens = {
      accessToken,
      refreshToken: refreshToken || undefined,
    };
    
    // Calculate expiration timestamp if expiresIn is provided
    if (expiresIn) {
      const expiresInSeconds = parseInt(expiresIn, 10);
      if (!isNaN(expiresInSeconds)) {
        tokens.expiresAt = Date.now() + (expiresInSeconds * 1000);
      }
    }
    
    return tokens;
  } catch (error) {
    logger.error('[Desktop Auth] Failed to parse deep link URL:', error);
    return null;
  }
}

/**
 * Initiate desktop login by opening the OAuth URL in the external browser
 * @param authUrl The OAuth authorization URL to open
 * @deprecated Use inAppOAuthFlow for in-app OAuth experience
 */
export async function initiateDesktopLogin(authUrl: string): Promise<void> {
  if (!isTauri()) {
    throw new Error('initiateDesktopLogin can only be used in desktop mode');
  }
  await openExternal(authUrl);
}

/**
 * OAuth callback data from Rust
 */
interface OAuthCallbackData {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

/**
 * Open an in-app OAuth window and wait for the callback
 * This provides a native-feeling OAuth experience without opening external browser
 *
 * @param authUrl The OAuth authorization URL
 * @param callbackUrlPattern Pattern to match for callback URL detection
 * @param timeoutMs Timeout in milliseconds (default: 5 minutes)
 * @returns Promise resolving to OAuth callback data (code + state)
 */
export async function inAppOAuthFlow(
  authUrl: string,
  callbackUrlPattern: string = '/auth/callback',
  timeoutMs: number = 300000
): Promise<{ code: string; state?: string }> {
  if (!isTauri()) {
    throw new Error('inAppOAuthFlow can only be used in desktop mode');
  }

  const { invoke } = await import('@tauri-apps/api/core');
  const { listen } = await import('@tauri-apps/api/event');

  return new Promise((resolve, reject) => {
    let unlistenCallback: (() => void) | null = null;
    let unlistenCancelled: (() => void) | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    // Cleanup function
    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (unlistenCallback) unlistenCallback();
      if (unlistenCancelled) unlistenCancelled();
    };

    // Set up timeout
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('OAuth flow timed out'));
    }, timeoutMs);

    // Listen for OAuth callback
    listen<OAuthCallbackData>('oauth-callback', (event) => {
      cleanup();

      const data = event.payload;
      logger.info('[inAppOAuthFlow] Received OAuth callback', { hasCode: !!data.code, hasError: !!data.error });

      if (data.error) {
        reject(new Error(data.error_description || data.error || 'OAuth failed'));
        return;
      }

      if (!data.code) {
        reject(new Error('No authorization code received'));
        return;
      }

      resolve({ code: data.code, state: data.state });
    }).then(unlisten => {
      unlistenCallback = unlisten;
    });

    // Listen for OAuth window being cancelled/closed
    listen<string>('oauth-cancelled', () => {
      cleanup();
      reject(new Error('OAuth flow was cancelled'));
    }).then(unlisten => {
      unlistenCancelled = unlisten;
    });

    // Open the OAuth window
    invoke('open_oauth_window', {
      authUrl,
      callbackUrlPattern,
    }).catch((error) => {
      cleanup();
      logger.error('[inAppOAuthFlow] Failed to open OAuth window:', error);
      reject(error);
    });
  });
}

/**
 * Handle the complete desktop auth flow
 * 1. Opens OAuth URL in external browser
 * 2. Waits for deep link callback with tokens
 * 3. Stores tokens securely
 * @returns Promise that resolves with tokens or rejects on timeout/error
 */
export async function desktopAuthFlow(
  authUrl: string,
  timeoutMs: number = 300000,
  options?: { forceNewAccount?: boolean }
): Promise<DesktopAuthTokens> {
  if (!isTauri()) {
    throw new Error('desktopAuthFlow can only be used in desktop mode');
  }

  // Ensure deep link listener is set up before creating the promise
  await setupDeepLinkAuthListener();

  return new Promise((resolve, reject) => {
    // Set up timeout
    const timeout = setTimeout(() => {
      unsubscribe();
      reject(new Error('Desktop auth flow timed out'));
    }, timeoutMs);

    // Listen for deep link callback
    const unsubscribe = onDeepLinkAuth((url) => {
      clearTimeout(timeout);
      unsubscribe();

      const tokens = parseDeepLinkAuthTokens(url);
      if (tokens) {
        // Store tokens securely
        const accountHint = options?.forceNewAccount
          ? { id: `${UNKNOWN_ACCOUNT_PREFIX}${Date.now()}` }
          : undefined;
        desktopSecureStorage.saveTokens(tokens, accountHint)
          .then(() => resolve(tokens))
          .catch((error) => {
            logger.error('[Desktop Auth] Failed to store tokens:', error);
            reject(error);
          });
      } else {
        reject(new Error('Invalid auth callback - no tokens found'));
      }
    });

    // Open OAuth URL in external browser
    initiateDesktopLogin(authUrl).catch((error) => {
      clearTimeout(timeout);
      unsubscribe();
      reject(error);
    });
  });
}

/**
 * Clear WebView cookies and browsing data
 * Useful for clearing stale auth cookies on desktop app startup or after auth failures
 */
export async function clearWebViewCookies(): Promise<void> {
  if (!isTauri()) return;

  try {
    const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
    const webview = getCurrentWebviewWindow();
    await webview.clearAllBrowsingData();
  } catch (error) {
    logger.error('[clearWebViewCookies] Failed to clear WebView cookies:', error);
  }
}

/**
 * Get the deep link scheme based on the Tauri app configuration
 * Returns 'scrambl' for production, 'scrambl-dev' for dev builds, 'scrambl-local' for local builds
 */
export async function getDesktopDeepLinkScheme(): Promise<string> {
  if (!isTauri()) {
    return 'scrambl';
  }

  try {
    const { getName } = await import('@tauri-apps/api/app');
    const appName = await getName();
    
    // Determine scheme based on app name
    if (appName.includes('(local)')) {
      return 'scrambl-local';
    } else if (appName.includes('(dev)')) {
      return 'scrambl-dev';
    }
    return 'scrambl';
  } catch (error) {
    logger.error('[Tauri] Failed to get app name:', error);
    return 'scrambl';
  }
}

/**
 * Lazy load Tauri APIs only when needed
 * This prevents build errors when running in the browser
 */
export async function getTauriAPI() {
  if (!isTauri()) {
    throw new Error('Tauri API is only available in desktop mode');
  }

  try {
    const [core, dialog, fs, notification] = await Promise.all([
      import('@tauri-apps/api/core'),
      import('@tauri-apps/plugin-dialog'),
      import('@tauri-apps/plugin-fs'),
      import('@tauri-apps/plugin-notification'),
    ]);

    return {
      core,
      dialog,
      fs,
      notification,
    };
  } catch (error) {
    logger.error('Failed to load Tauri API:', error);
    throw error;
  }
}

// App hosts that should stay in the webview
const APP_HOSTS = [
  'localhost',
  '127.0.0.1',
  'scrambl.com',
  'app.scrambl.com',
  'dev.scrambl.com',
  'api.scrambl.com',
  'dev-api.scrambl.com',
];

/**
 * Check if a URL is external (should open in default browser)
 */
export function isExternalUrl(url: string): boolean {
  try {
    // Relative URLs are internal - these should be handled by React Router
    if (url.startsWith('/') || url.startsWith('#') || url.startsWith('?') || url.startsWith('./') || url.startsWith('../')) {
      return false;
    }
    
    // Blob URLs are for downloads, not external navigation
    if (url.startsWith('blob:')) {
      return false;
    }
    
    // JavaScript URLs should not be intercepted
    if (url.startsWith('javascript:')) {
      return false;
    }
    
    // mailto: and tel: should open externally
    if (url.startsWith('mailto:') || url.startsWith('tel:')) {
      return true;
    }
    
    // URLs without a protocol are relative paths (e.g., "some-page" or "folder/page")
    // These should be handled by React Router
    if (!url.includes('://')) {
      return false;
    }
    
    const parsedUrl = new URL(url, window.location.origin);

    // Non-http schemes should open externally (except the ones we handled above)
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return true;
    }

    // Check if it's NOT one of our app hosts
    const host = parsedUrl.hostname;
    return !APP_HOSTS.some(appHost =>
      host === appHost || host.endsWith(`.${appHost}`)
    );
  } catch {
    return false;
  }
}


// Cache the popup window status to avoid repeated checks
let cachedIsPopupWindow: boolean | null = null;

/**
 * Check if the current window is a popup window (chromeless mode)
 * Popup windows have ?popup=true in the URL and should hide app navigation.
 * Tray popovers are treated as popup windows as well.
 * The result is cached since the window type doesn't change during the session
 */
export function isPopupWindow(): boolean {
  if (typeof window === 'undefined') return false;

  // Return cached value if available
  if (cachedIsPopupWindow !== null) {
    return cachedIsPopupWindow;
  }

  // Check URL parameter (works for initial load)
  const params = new URLSearchParams(window.location.search);
  if (params.get('popup') === 'true') {
    cachedIsPopupWindow = true;
    return true;
  }

  if (window.location.pathname === '/tray-tasks') {
    cachedIsPopupWindow = true;
    return true;
  }

  if (window.location.pathname === '/benny-companion') {
    cachedIsPopupWindow = true;
    return true;
  }

  cachedIsPopupWindow = false;
  return false;
}

/**
 * Initialize popup window detection - call this early in app startup
 * Checks both URL parameter and Tauri window label for focus windows
 */
export async function initializePopupWindowStatus(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  // A synchronous URL check can conclusively identify a popup, but a false
  // result is only provisional in Tauri. Restored focus windows do not always
  // retain popup=true in the URL, so their focus-* label must still be checked.
  if (cachedIsPopupWindow === true) {
    return true;
  }

  // Check URL parameter first
  const params = new URLSearchParams(window.location.search);
  if (params.get('popup') === 'true') {
    cachedIsPopupWindow = true;
    return true;
  }

  if (window.location.pathname === '/tray-tasks') {
    cachedIsPopupWindow = true;
    return true;
  }

  if (window.location.pathname === '/benny-companion') {
    cachedIsPopupWindow = true;
    return true;
  }

  // For Tauri, also check if this is a focus window by label
  if (isTauri()) {
    try {
      const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
      const currentWindow = getCurrentWebviewWindow();
      const label = currentWindow.label;
      if (label === 'tray-popover' || label === 'benny-companion' || label === 'settings') {
        cachedIsPopupWindow = true;
        return true;
      }
      if (label.startsWith('focus-') || label.startsWith('popup-')) {
        cachedIsPopupWindow = true;
        return true;
      }
      if (label === 'main' || label.startsWith('main-')) {
        cachedIsPopupWindow = false;
        return false;
      }
    } catch {
      // Ignore errors
    }
  }

  cachedIsPopupWindow = false;
  return false;
}

/**
 * Focus window configuration (matches Rust FocusWindowConfig)
 */
export interface FocusWindow {
  id: string;
  path: string;
  title: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

/**
 * Open a module page in a persistent focus window (desktop only)
 * Focus windows are restored on app restart unless manually closed.
 * If a focus window for the same path already exists, it will be focused instead of creating a new one.
 *
 * @param path - The app path to open (e.g., "/acme/tasks")
 * @param title - The window title (e.g., "Tasks")
 * @returns The window ID if successful
 */
export async function openFocusWindow(path: string, title: string): Promise<string | null> {
  if (!isTauri()) {
    // In browser, just open a new tab
    const url = new URL(`${window.location.origin}${path}`);
    url.searchParams.set('popup', 'true');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
    return null;
  }

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const windowId = await invoke<string>('open_focus_window', { path, title });
    logger.info(`Opened focus window: ${windowId}`);
    return windowId;
  } catch (error) {
    logger.error('Failed to open focus window:', error);
    // Fallback to regular popup window
    await openInNewWindow(path, title);
    return null;
  }
}

/**
 * Restore all saved focus windows on app startup
 * This should be called once from the main window when the app loads.
 *
 * @returns The number of windows restored
 */
export async function restoreFocusWindows(): Promise<number> {
  if (!isTauri()) return 0;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const count = await invoke<number>('restore_focus_windows');
    logger.info(`Restored ${count} focus windows`);
    return count;
  } catch (error) {
    logger.error('Failed to restore focus windows:', error);
    return 0;
  }
}

/**
 * Get all saved focus windows
 *
 * @returns Array of focus window configurations
 */
export async function getFocusWindows(): Promise<FocusWindow[]> {
  if (!isTauri()) return [];

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    return await invoke<FocusWindow[]>('get_focus_windows');
  } catch (error) {
    logger.error('Failed to get focus windows:', error);
    return [];
  }
}

/**
 * Check if the current window is a focus window
 * Focus windows have labels starting with "focus-"
 */
export async function isFocusWindow(): Promise<boolean> {
  if (!isTauri()) return false;

  try {
    const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
    const currentWindow = getCurrentWebviewWindow();
    return currentWindow.label.startsWith('focus-');
  } catch {
    return false;
  }
}

/**
 * Check if the current window is the main window
 */
export async function isMainWindow(): Promise<boolean> {
  if (!isTauri()) return true; // In browser, treat as main

  try {
    const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
    const currentWindow = getCurrentWebviewWindow();
    return currentWindow.label === 'main';
  } catch {
    return true;
  }
}

/**
 * Open an internal app URL in a new Tauri window (for desktop) or new tab (for web)
 * Use this for opening app pages like tasks in a separate window
 * Adds ?popup=true to hide app navigation in the new window
 */
export async function openInNewWindow(path: string, title?: string): Promise<void> {
  // Build the full URL from the path, adding popup=true query param
  const url = new URL(path.startsWith('http') ? path : `${window.location.origin}${path}`);
  url.searchParams.set('popup', 'true');
  const fullUrl = url.toString();
  const appPath = `${url.pathname}${url.search}${url.hash}`;
  
  if (isTauri()) {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const isInternal = url.origin === window.location.origin;
      if (isInternal) {
        await invoke('open_internal_window', { path: appPath, title });
        return;
      }

      await invoke('open_popup_window', { url: fullUrl, title });
    } catch (error) {
      logger.error('Failed to open internal URL in new window:', error);
      // Fallback to window.open
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  } else {
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Open a full app window dedicated to an organization or client portal.
 * Desktop windows are keyed by context, so reopening the same context focuses
 * its existing window even if that window has navigated to another page.
 */
export async function openWorkspaceWindow(
  contextKey: string,
  path: string,
  title: string
): Promise<string | null> {
  if (!isTauri()) {
    const url = path.startsWith('http') ? path : `${window.location.origin}${path}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    return null;
  }

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const windowId = await invoke<string>('open_workspace_window', {
      contextKey,
      path,
      title,
    });
    logger.info(`Opened workspace window: ${windowId}`);
    return windowId;
  } catch (error) {
    logger.error('Failed to open workspace window:', error);
    return null;
  }
}

/**
 * Open an internal Scrambl path from an "Open in Tab"-style action.
 * On desktop this opens a native popup window (openInNewWindow); on web it
 * opens a plain new browser tab WITHOUT the popup=true chrome-stripping param,
 * so web behavior stays a normal full-app tab.
 * External URLs should use openExternal() instead.
 */
export async function openInternalPath(path: string, title?: string): Promise<void> {
  if (isTauri()) {
    await openInNewWindow(path, title);
    return;
  }
  const url = path.startsWith('http') ? path : `${window.location.origin}${path}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Open an external URL in the system's default browser
 * This is used for non-Scrambl URLs (Google sign-in, external sites, etc.)
 * For internal Scrambl URLs in a new window, use openInNewWindow() instead
 */
export async function openExternal(url: string): Promise<void> {
  if (isTauri()) {
    try {
      const { openUrl } = await import('@tauri-apps/plugin-opener');
      await openUrl(url);
    } catch (error) {
      logger.error('Failed to open external URL:', error);
      // Fallback to window.open
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Save the current path for the main window (for page restoration on app restart)
 * This should only be called from the main window, not from focus/popup windows
 *
 * @param path - The current path (e.g., "/acme/tasks")
 */
export async function saveMainWindowPath(path: string): Promise<void> {
  if (!isTauri()) return;

  // Don't let transient boot/auth routes replace the last meaningful place.
  // Stable global pages (Settings, Help, What's New, etc.) are intentionally
  // persisted alongside organization-scoped routes.
  const excludedPaths = ['/login', '/register', '/auth', '/unauthorized', '/invite', '/organizations', '/organization-setup', '/user-setup', '/subscription-setup'];
  if (excludedPaths.some(excluded => path.startsWith(excluded))) {
    return;
  }

  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\') || path === '/') {
    return;
  }

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('save_main_window_path', { path });
  } catch (error) {
    logger.error('Failed to save main window path:', error);
  }
}

/**
 * Get the last saved path for the main window
 *
 * @returns The last saved path, or null if none exists
 */
export async function getMainWindowPath(): Promise<string | null> {
  if (!isTauri()) return null;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    return await invoke<string | null>('get_main_window_path');
  } catch (error) {
    logger.error('Failed to get main window path:', error);
    return null;
  }
}

/**
 * Save a one-time path to restore after an updater-triggered app relaunch.
 * Unlike normal route tracking, this supports stable global routes such as /settings.
 */
export async function saveUpdateRestartPath(path: string): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('save_update_restart_path', { path });
  } catch (error) {
    logger.error('Failed to save update restart path:', error);
  }
}

/**
 * Read and clear the one-time updater return path.
 */
export async function takeUpdateRestartPath(): Promise<string | null> {
  if (!isTauri()) return null;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    return await invoke<string | null>('take_update_restart_path');
  } catch (error) {
    logger.error('Failed to take update restart path:', error);
    return null;
  }
}

/**
 * Open a new main window (desktop only)
 *
 * @returns The window ID if successful
 */
export async function openNewWindow(): Promise<string | null> {
  if (!isTauri()) {
    // In browser, just open a new tab at home
    window.open(window.location.origin, '_blank');
    return null;
  }

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const windowId = await invoke<string>('open_new_window');
    logger.info(`Opened new window: ${windowId}`);
    return windowId;
  } catch (error) {
    logger.error('Failed to open new window:', error);
    return null;
  }
}

export interface BennyCompanionSettings {
  enabled: boolean;
  x?: number | null;
  y?: number | null;
}

export async function getBennyCompanionSettings(): Promise<BennyCompanionSettings | null> {
  if (!isTauri()) return null;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    return await invoke<BennyCompanionSettings>('get_benny_companion_settings');
  } catch (error) {
    logger.error('Failed to get Benny companion settings:', error);
    return null;
  }
}

export async function setBennyCompanionEnabled(enabled: boolean): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('set_benny_companion_enabled', { enabled });
  } catch (error) {
    logger.error('Failed to save Benny companion setting:', error);
  }
}

export async function showBennyCompanion(): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('show_benny_companion');
  } catch (error) {
    logger.error('Failed to show Benny companion:', error);
  }
}

export async function hideBennyCompanion(shouldDisable = false): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('hide_benny_companion', { shouldDisable });
  } catch (error) {
    logger.error('Failed to hide Benny companion:', error);
  }
}

/**
 * Bring the main desktop window to the foreground (show, unminimize, focus).
 * Must be called from the main window's webview.
 */
export async function focusMainWindow(): Promise<void> {
  if (!isTauri()) return;

  try {
    const { getCurrentWindow } = await import('@tauri-apps/api/window');
    const window = getCurrentWindow();
    await window.show();
    await window.unminimize();
    await window.setFocus();
  } catch (error) {
    logger.error('Failed to focus main window:', error);
  }
}

export async function applyBennyCompanionState(enabled: boolean): Promise<void> {
  if (!isTauri()) return;

  if (enabled) {
    await setBennyCompanionEnabled(true);
    await showBennyCompanion();
  } else {
    // hide with shouldDisable=true persists enabled=false AND emits the
    // 'benny-companion-disabled' event so the main window clears
    // companionActive (otherwise native notifications stay suppressed).
    await hideBennyCompanion(true);
  }
}

/**
 * Clear all app data (localStorage, sessionStorage, auth tokens, IndexedDB, WebView cookies)
 * This is used for troubleshooting and logging out completely
 */
export async function clearAppData(): Promise<void> {

  // 1. Clear localStorage
  try {
    localStorage.clear();
  } catch (error) {
    logger.error('[clearAppData] Failed to clear localStorage:', error);
  }

  // 2. Clear sessionStorage
  try {
    sessionStorage.clear();
  } catch (error) {
    logger.error('[clearAppData] Failed to clear sessionStorage:', error);
  }

  // 3. Clear IndexedDB databases
  try {
    if ('indexedDB' in window) {
      const databases = await window.indexedDB.databases();
      for (const db of databases) {
        if (db.name) {
          window.indexedDB.deleteDatabase(db.name);
        }
      }
    }
  } catch (error) {
    logger.error('[clearAppData] Failed to clear IndexedDB:', error);
  }

  // 4. Clear desktop auth accounts if in Tauri
  if (isTauri()) {
    try {
      await desktopSecureStorage.clearAllAccounts();
    } catch (error) {
      logger.error('[clearAppData] Failed to clear desktop tokens:', error);
    }

    // 5. Clear WebView cookies and all browsing data (Tauri 2.4.0+)
    try {
      const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
      const webview = getCurrentWebviewWindow();
      await webview.clearAllBrowsingData();
    } catch (error) {
      logger.error('[clearAppData] Failed to clear WebView browsing data:', error);
    }
  }

  // 6. Clear service worker caches
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }
    }
  } catch (error) {
    logger.error('[clearAppData] Failed to clear caches:', error);
  }
}

// ==========================================
// Tray Popover Utilities
// ==========================================

/**
 * Sync the current organization slug with the Tauri backend
 * This allows the tray popover to know which organization to display
 */
export async function syncOrganizationSlug(slug: string): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('sync_organization_slug', { slug });
    logger.debug('[syncOrganizationSlug] Synced org slug:', slug);
  } catch (error) {
    logger.error('[syncOrganizationSlug] Failed to sync org slug:', error);
  }
}

/**
 * Get the currently synced organization slug from Tauri store
 */
export async function getSyncedOrganizationSlug(): Promise<string | null> {
  if (!isTauri()) return null;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const slug = await invoke<string | null>('get_synced_organization_slug');
    return slug;
  } catch (error) {
    logger.error('[getSyncedOrganizationSlug] Failed to get org slug:', error);
    return null;
  }
}

/**
 * Show the tray popover window
 */
export async function showTrayPopover(): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('show_tray_popover_command');
  } catch (error) {
    logger.error('[showTrayPopover] Failed to show tray popover:', error);
  }
}

/**
 * Hide the tray popover window
 */
export async function hideTrayPopover(): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('hide_tray_popover_command');
  } catch (error) {
    logger.error('[hideTrayPopover] Failed to hide tray popover:', error);
  }
}

/**
 * Check if the tray popover is visible
 */
export async function isTrayPopoverVisible(): Promise<boolean> {
  if (!isTauri()) return false;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    return await invoke<boolean>('is_tray_popover_visible');
  } catch (error) {
    logger.error('[isTrayPopoverVisible] Failed to check tray popover visibility:', error);
    return false;
  }
}

/**
 * Check if current window is the tray popover
 */
export function isTrayPopoverWindow(): boolean {
  if (!isTauri()) return false;

  try {
    // Check URL for tray-tasks path
    return window.location.pathname.includes('/tray-tasks');
  } catch {
    return false;
  }
}

/**
 * Listen for organization slug changes from other windows
 */
export async function listenForOrgSlugChanges(
  callback: (slug: string) => void
): Promise<() => void> {
  if (!isTauri()) return () => {};

  try {
    const { listen } = await import('@tauri-apps/api/event');
    const unlisten = await listen<string>('org-slug-changed', (event) => {
      callback(event.payload);
    });
    return unlisten;
  } catch (error) {
    logger.error('[listenForOrgSlugChanges] Failed to listen for org slug changes:', error);
    return () => {};
  }
}

// ==========================================
// Native Utility Layer
// ==========================================

/**
 * Listen for the global quick-capture hotkey (CommandOrControl+Shift+K).
 *
 * Rust registers the system-wide shortcut, shows/focuses the main window and
 * then emits 'quick-capture' to it. Registration is best-effort on the Rust
 * side, so this listener may simply never fire if another app owns the
 * combination — that is not an error condition.
 */
export async function listenForQuickCapture(
  callback: () => void
): Promise<() => void> {
  if (!isTauri()) return () => {};

  try {
    const { listen } = await import('@tauri-apps/api/event');
    const unlisten = await listen<void>('quick-capture', () => {
      callback();
    });
    return unlisten;
  } catch (error) {
    logger.error('[listenForQuickCapture] Failed to listen for quick capture:', error);
    return () => {};
  }
}

/**
 * Set the text shown next to the tray icon in the macOS menu bar.
 * Pass null or an empty string to clear it. Windows has no tray title, where
 * the Rust command is a no-op.
 */
export async function setTrayTitle(title: string | null): Promise<void> {
  if (!isTauri()) return;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('set_tray_title', { title: title || null });
  } catch (error) {
    logger.error('[setTrayTitle] Failed to set tray title:', error);
  }
}

export interface NativeNotificationOptions {
  /**
   * Opaque handle the shell hands back on 'notification-activated' when the
   * user clicks the notification. The caller uses it to look the notification's
   * in-app destination back up.
   */
  notificationId: string;
  title: string;
  body?: string;
  /** Only used by the plugin fallback — macOS shows the app's own icon. */
  icon?: string;
  sound?: string;
}

/**
 * Deliver a desktop notification through the Rust shell so that clicking it
 * focuses the app and navigates to the record it is about.
 *
 * `tauri-plugin-notification` cannot do this: only its mobile implementation
 * emits `actionPerformed`, so a click on a desktop notification is dropped.
 * The shell delivers macOS notifications itself and falls back to the plugin
 * everywhere else (and on any macOS failure), so a notification is always
 * delivered even when click-through is not available.
 *
 * Returns false only when the shell command could not be reached at all — an
 * older desktop binary, say — so the caller can fall back to the plugin.
 */
export async function sendNativeNotification(
  options: NativeNotificationOptions
): Promise<boolean> {
  if (!isTauri()) return false;

  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('notify_with_link', {
      notificationId: options.notificationId,
      title: options.title,
      body: options.body ?? null,
      icon: options.icon ?? null,
      sound: options.sound ?? null,
    });
    return true;
  } catch (error) {
    logger.warn('[sendNativeNotification] Shell notification unavailable:', error);
    return false;
  }
}

/**
 * Listen for clicks on notifications sent via sendNativeNotification.
 *
 * The shell has already brought the main window forward by the time this
 * fires; the payload is the notificationId that was passed to
 * sendNativeNotification.
 */
export async function listenForNotificationActivated(
  callback: (notificationId: string) => void
): Promise<() => void> {
  if (!isTauri()) return () => {};

  try {
    const { listen } = await import('@tauri-apps/api/event');
    const unlisten = await listen<string>('notification-activated', (event) => {
      callback(event.payload);
    });
    return unlisten;
  } catch (error) {
    logger.error(
      '[listenForNotificationActivated] Failed to listen for notification clicks:',
      error
    );
    return () => {};
  }
}

/**
 * Listen for tray popover shown events
 * This fires when the tray popover becomes visible (gains focus)
 */
export async function listenForTrayPopoverShown(
  callback: () => void
): Promise<() => void> {
  if (!isTauri()) return () => {};

  try {
    const { listen } = await import('@tauri-apps/api/event');
    const unlisten = await listen<void>('tray-popover-shown', () => {
      callback();
    });
    return unlisten;
  } catch (error) {
    logger.error('[listenForTrayPopoverShown] Failed to listen for tray popover shown:', error);
    return () => {};
  }
}
